import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  CheckCircle2, 
  DollarSign,
  Phone,
  ArrowLeft,
  Heart,
  Scale,
  Award,
  Users,
  ShieldCheck,
  BarChart3
} from "lucide-react";

const statistics = [
  { value: "40%", label: "הפחתה בתאונות עבודה", description: "ארגונים עם ממונה בטיחות פעיל" },
  { value: "₪1.2M", label: "עלות ממוצעת לתאונה חמורה", description: "כולל אובדן ימי עבודה ופיצויים" },
  { value: "85%", label: "מהתאונות ניתנות למניעה", description: "באמצעות ניהול סיכונים נכון" },
  { value: "3:1", label: "יחס עלות-תועלת", description: "כל שקל בבטיחות חוסך שלושה" }
];

const businessBenefits = [
  {
    icon: DollarSign,
    title: "חיסכון כספי משמעותי",
    points: [
      "הפחתת עלויות תאונות עבודה",
      "מניעת קנסות ממשרד העבודה",
      "הורדת דמי ביטוח לאומי",
      "צמצום ימי היעדרות"
    ]
  },
  {
    icon: Scale,
    title: "עמידה בדרישות החוק",
    points: [
      "עמידה בפקודת הבטיחות בעבודה",
      "מניעת תביעות משפטיות",
      "הגנה על ההנהלה מאחריות אישית",
      "מוכנות לביקורות"
    ]
  },
  {
    icon: Heart,
    title: "רווחת העובדים",
    points: [
      "סביבת עבודה בטוחה",
      "שיפור מורל העובדים",
      "הפחתת תחלופת עובדים",
      "משיכת עובדים איכותיים"
    ]
  },
  {
    icon: Award,
    title: "מוניטין ותדמית",
    points: [
      "תדמית של ארגון אחראי",
      "יתרון במכרזים",
      "שיפור יחסי ציבור",
      "אמינות מול לקוחות"
    ]
  }
];

const riskOfNotHaving = [
  {
    title: "סיכונים משפטיים",
    description: "קנסות של עד 226,000 ₪, אחריות אישית של המנהלים, ואפשרות לסגירת העסק",
    severity: "גבוה"
  },
  {
    title: "סיכונים כלכליים",
    description: "עלות תאונות, פיצויים, אובדן ימי עבודה, ונזק למוניטין",
    severity: "גבוה"
  },
  {
    title: "סיכונים תפעוליים",
    description: "הפסקת עבודה בעקבות תאונות, עיכובים בפרויקטים",
    severity: "בינוני"
  },
  {
    title: "סיכונים ארגוניים",
    description: "ירידה במורל, תחלופת עובדים גבוהה, קושי בגיוס",
    severity: "בינוני"
  }
];

const caseStudies = [
  {
    industry: "תעשייה",
    before: "12 תאונות בשנה, קנסות של 150,000 ₪",
    after: "2 תאונות בשנה, אפס קנסות",
    savings: "חיסכון של 400,000 ₪ בשנה"
  },
  {
    industry: "בנייה",
    before: "צו הפסקת עבודה, פיגור בפרויקט",
    after: "עמידה מלאה בתקנות, לוחות זמנים",
    savings: "מניעת הפסד של 2 מיליון ₪"
  },
  {
    industry: "לוגיסטיקה",
    before: "ימי היעדרות גבוהים, תחלופת עובדים",
    after: "הפחתה של 60% בהיעדרויות",
    savings: "שיפור פרודוקטיביות של 25%"
  }
];

export default function SafetyOfficerImportance() {
  return (
    <Layout>
      <Helmet>
        <title>חשיבות ממונה הבטיחות בארגון | Adam Safety</title>
        <meta name="description" content="למה ממונה בטיחות חיוני לארגון שלך? נתונים, יתרונות עסקיים והשפעה על שורת הרווח. מדריך מקיף לקבלת החלטות." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">ערך עסקי</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              חשיבות ממונה הבטיחות בארגון
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              מדוע השקעה בבטיחות היא ההשקעה הכי משתלמת שתעשו - 
              נתונים, יתרונות עסקיים והשפעה על שורת הרווח
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  שיחה עם מומחה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              המספרים מדברים
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              נתונים שמוכיחים את הערך העסקי של ניהול בטיחות מקצועי
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {statistics.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6 text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">{stat.value}</div>
                <div className="text-lg font-bold text-foreground mb-1">{stat.label}</div>
                <p className="text-sm text-muted-foreground">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              יתרונות עסקיים של ממונה בטיחות
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ממונה בטיחות אינו רק דרישת חוק - הוא השקעה שמחזירה את עצמה
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {businessBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="icon-container mb-4">
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4">{benefit.title}</h3>
                <ul className="space-y-2">
                  {benefit.points.map((point, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Risks Section */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              הסיכונים בהיעדר ממונה בטיחות
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              מה יכול לקרות כשאין ניהול בטיחות מקצועי
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {riskOfNotHaving.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 border border-red-200 dark:border-red-800"
              >
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-red-700 dark:text-red-400">{risk.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    risk.severity === 'גבוה' 
                      ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' 
                      : 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                  }`}>
                    סיכון {risk.severity}
                  </span>
                </div>
                <p className="text-red-800 dark:text-red-200">{risk.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סיפורי הצלחה מהשטח
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              דוגמאות אמיתיות לשינוי שממונה בטיחות יכול לחולל
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent mb-4">
                  <span className="text-sm font-medium">{study.industry}</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">לפני:</p>
                    <p className="text-muted-foreground text-sm">{study.before}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">אחרי:</p>
                    <p className="text-muted-foreground text-sm">{study.after}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-border">
                    <p className="text-accent font-bold">{study.savings}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              רוצים לדעת כמה תחסכו?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              בואו לשיחה קצרה ונחשב יחד את החיסכון הפוטנציאלי לארגון שלכם
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  חישוב ROI חינם
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  מידע על השירות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}